// UploadModal — multi-file photo picker for a single album (W3 §6.4).
//
// Apple HIG pattern: mobile BottomSheet that opens from the
// `+ 新增相片` bar button in AlbumDetail.  Per-file states follow the
// spec: queued → uploading → done | error.  Progress is approximated by
// a CSS transition (we don't have XHR upload events on the openapi-fetch
// client, so we use indeterminate shimmer during upload).
//
// On success the parent invalidates `useAlbumItems` so the grid re-fetches.
// On close (success) we auto-close 800ms after the last success (per
// w3.md §6.4 last bullet).
import { useCallback, useRef, useState } from "react";
import { Camera, X, FolderOpen, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils/cn";
import { ApiError } from "@/lib/api/pair";
import { uploadMedia, type AlbumItemRead } from "@/lib/api/album";
import { compressImage } from "@/lib/albums/compressImage";
import { MAX_UPLOAD_BYTES } from "@/lib/apiLimits";
import { useJsonDraft } from "@/lib/hooks/useDraft";

/**
 * 50 MB — the backend's own per-file cap (`MAX_BYTES`,
 * `backend/app/modules/album/storage.py:34`), which is also what w3.md §5.2
 * specifies.
 *
 * W52.5: this used to be 25 MB, described as 「production 拒绝的请求上限」,
 * and the sentence underneath it measured the 413 to prove the number was
 * real. The measurement was right; the conclusion was wrong. That 413 came
 * from `client_max_body_size 25m`, whose stated justification — 「后端本身
 * 不限制体积」 — had been false since W39.69. So the client was gating at a
 * number no backend rule asked for, against formats it cannot compress:
 *
 *   production 2026-10-13, one pick of three photos → 1 uploaded, 2 refused,
 *   and **zero log entries** for the two refusals (access / error /
 *   journalctl). A body that reached nginx and was too big leaves a 413 —
 *   the user's own 36,143,681-byte attempt is in the error log from 10-12.
 *   Nothing at all means `fetch` was never called.
 *
 * nginx is now 52 MB (50 + multipart framing), so the request ceiling and the
 * per-file ceiling are one number again and this gate states the backend's
 * rule instead of nginx's.
 */
const MAX_BYTES = MAX_UPLOAD_BYTES;
const MAX_MB = MAX_BYTES / (1024 * 1024);

const ALLOWED_MIMES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "video/mp4",
  "video/quicktime",
];

/**
 * 413 is the one upload failure whose body is not this app's error envelope.
 * nginx answers it itself, with an HTML page, so `ApiError.code` is the generic
 * fallback and `ApiError.message` is generic fallback text — the branch has to
 * key on the status. Same cap as `MAX_BYTES` above, quoted from the same line.
 */
function uploadErrorMessage(err: unknown): string {
  if (err instanceof ApiError && err.status === 413) {
    return `檔案超過 ${MAX_MB} MB 上限，請選擇較小的檔案`;
  }
  return err instanceof ApiError ? err.message : "上傳失敗";
}

export interface UploadModalAlbumOption {
  id: string;
  name: string;
  is_system?: boolean;
}

type FileState = "queued" | "uploading" | "done" | "error";

interface UploadEntry {
  id: string;
  name: string;
  size: number;
  /** Set once compression has run, when it actually shrank something. */
  originalSize?: number;
  state: FileState;
  error?: string;
}

interface UploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Currently-shown album (the one `+ 新增相片` was tapped on). */
  defaultAlbumId: string;
  /** All pair albums so user can route to a different one. */
  albumOptions: UploadModalAlbumOption[];
  /**
   * Called after the queue is fully drained (success or cancel).
   *
   * W52.1 D-4 — receives the rows the server created. `AlbumDetail` ignores the
   * argument and refetches; `DiaryEditor` **cannot**: it is composing a diary,
   * not standing on an album page, so the photos have to reach its own state
   * to become `media_ids` on the submit payload. Returning nothing would leave
   * the photo in the album and absent from the diary — uploaded, invisible,
   * unattached, with no error anywhere.
   */
  onUploaded?: (items: AlbumItemRead[]) => void;
}

export function UploadModal({
  open,
  onOpenChange,
  defaultAlbumId,
  albumOptions,
  onUploaded,
}: UploadModalProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<UploadEntry[]>([]);
  /** W52.1 D-4 — the rows the server actually created, accumulated across
   *  batches so a second pick in the same sheet adds to the first. */
  const createdRef = useRef<AlbumItemRead[]>([]);

  // W56.0 I — the draft.
  //
  // **Partial on purpose, and the limit is the platform's**: the *chosen
  // album* survives a redirect, the picked **files cannot** — a `File` is not
  // serialisable and `sessionStorage` holds strings, so the queue has to be
  // re-picked. Losing the album choice is the part that is cheap to fix and
  // annoying to lose, so that is what the draft carries.
  const uploadDraft = useJsonDraft<{ targetAlbumId: string }>("album-upload", {
    targetAlbumId: defaultAlbumId,
  });

  const [targetAlbumId, setTargetAlbumId] = useState<string>(
    uploadDraft.value.targetAlbumId,
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chooseAlbum = useCallback(
    (albumId: string) => {
      setTargetAlbumId(albumId);
      uploadDraft.save({ targetAlbumId: albumId });
    },
    [uploadDraft],
  );

  const reset = useCallback(() => {
    setQueue([]);
    setBusy(false);
    setError(null);
    setTargetAlbumId(defaultAlbumId);
    // …and the draft follows, so the two never disagree. The draft's job is to
    // survive a **redirect** (a remount), which never runs this reset; a
    // deliberate close resets both.
    uploadDraft.save({ targetAlbumId: defaultAlbumId });
    createdRef.current = [];
    if (fileInput.current) fileInput.current.value = "";
  }, [defaultAlbumId, uploadDraft]);

  const pickFiles = () => fileInput.current?.click();

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    // **MIME only.** The size gate runs *after* compression — see the note at the
    // loop below. Gating the raw size here was a real defect: it rejected
    // exactly the files compression exists to save. Production's nginx log for
    // 2026-10-12 has the user's own attempt:
    //
    //   client intended to send too large body: 36,143,681 bytes
    //   POST /api/v1/media  (Android Chrome, referrer /albums/…)
    //
    // A 34.5 MB JPEG is roughly 2 MB once it is 1920px on the long edge, so
    // that photo was refused by a check that ran before the work that would
    // have made it uploadable.
    const filtered = list.filter((f) => {
      if (!ALLOWED_MIMES.includes(f.type)) {
        setError("僅支援 JPEG / PNG / WebP / HEIC 圖片、MP4 / MOV 影片");
        return false;
      }
      return true;
    });
    if (filtered.length === 0) return;

    setError(null);
    const initialEntries: UploadEntry[] = filtered.map((f) => ({
      id: `${Date.now()}-${f.name}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name,
      size: f.size,
      state: "queued",
    }));
    setQueue((q) => [...q, ...initialEntries]);
    setBusy(true);

    // Upload one-by-one (openapi-fetch is single-flight per request; we
    // could parallelise but keeping order matches iOS Photos behaviour).
    for (let i = 0; i < filtered.length; i++) {
      const file = filtered[i]!;
      const entryId = initialEntries[i]!.id;
      setQueue((q) => q.map((e) => (e.id === entryId ? { ...e, state: "uploading" } : e)));
      try {
        // w50.1 §4.4 + §5: shrink before sending, so a batch of phone photos
        // stays clear of the 25 MB nginx ceiling instead of arriving as one
        // unreadable 413. Compression can only ever hand back the original
        // (HEIC, video, a decode failure), so this step cannot break an upload
        // that used to work.
        const prepared = await compressImage(file);
        // **The gate is here, on the prepared file** — not on the raw one in
        // the filter above. Compression's entire purpose is to bring an
        // oversized photo under the ceiling; a pre-compression gate refuses the
        // very files it exists to rescue. What is left here is the set it
        // genuinely cannot help: a HEIC or a video that Canvas cannot decode,
        // and which would otherwise come back from nginx as an HTML 413 page
        // with no way for the user to act on it.
        if (prepared.file.size > MAX_BYTES) {
          setQueue((q) =>
            q.map((e) =>
              e.id === entryId
                ? {
                    ...e,
                    name: prepared.file.name,
                    size: prepared.bytes,
                    originalSize: prepared.compressed ? prepared.originalBytes : undefined,
                    state: "error",
                    error: `檔案超過 ${MAX_MB} MB 上限，請選擇較小的檔案`,
                  }
                : e,
            ),
          );
          continue;
        }
        // The row shows what is actually going over the wire — which includes
        // the name, because a `.webp` carrying JPEG's stem-extension pair is
        // what the backend stores, and the old name would be a small lie.
        setQueue((q) =>
          q.map((e) =>
            e.id === entryId
              ? {
                  ...e,
                  name: prepared.file.name,
                  size: prepared.bytes,
                  originalSize: prepared.compressed ? prepared.originalBytes : undefined,
                }
              : e,
          ),
        );
        // W52.1 D-4 — the sheet now **uses** the return value (it hands the created
        // rows to `DiaryEditor`, which needs them to build `media_ids`), so
        // this call site is the first to depend on its shape.
        //
        // `Array.isArray` rather than `?? []`: a plain `?? []` accepts `{}`,
        // and spreading `{}` throws `Found non-callable @@iterator` — which
        // lands in the `catch` below and is reported to the user as
        // 「上傳失敗」 on a photo that uploaded fine. The upload succeeding and
        // the row claiming otherwise is the worst version of this bug, so the
        // shape is checked rather than assumed.
        const created = await uploadMedia([prepared.file], { albumId: targetAlbumId });
        if (Array.isArray(created)) createdRef.current.push(...created);
        setQueue((q) => q.map((e) => (e.id === entryId ? { ...e, state: "done" } : e)));
      } catch (err) {
        // The server's message, not its code. This was
        // `${err.code}: ${err.message}`, which showed a Traditional-Chinese
        // screen `ALBUM_FORBIDDEN: 你沒有權限` — the half the user cannot act on
        // in front of the half they can. Same defect as the wish photo block's
        // `W34.2.5` copy (ADR-0104), and the same fix: every error mapper in
        // `lib/i18n` already prefers `serverMessage` for this reason.
        // `uploadErrorMessage` adds the one status the server never gets to
        // speak for: a 413 is nginx's, not FastAPI's.
        const msg = uploadErrorMessage(err);
        setQueue((q) =>
          q.map((e) => (e.id === entryId ? { ...e, state: "error", error: msg } : e)),
        );
      }
    }
    setBusy(false);
    if (fileInput.current) fileInput.current.value = "";
    // W52.1 D-4 — hand back what the server made, not just "something happened".
    onUploaded?.(createdRef.current);
    // Auto-close 800ms after the last success (per w3.md §6.4).
    setTimeout(() => {
      onOpenChange(false);
      reset();
    }, 800);
  };

  const remove = (entryId: string) => {
    setQueue((q) => q.filter((e) => e.id !== entryId));
  };

  const doneCount = queue.filter((e) => e.state === "done").length;
  const errorCount = queue.filter((e) => e.state === "error").length;

  return (
    <BottomSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
      variant="mobile"
      title="新增相片"
      // W56.0 I — a multi-file picker is a form: a stray tap on the backdrop
      // must not throw away the queue.
      dismissibleOnBackdrop={false}
      data-testid="album-upload-modal"
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="font-label text-ink-2">選擇相簿</span>
          <div
            className="flex items-center gap-2 overflow-x-auto py-1"
            data-testid="upload-modal-album-row"
          >
            <FolderOpen size={14} className="text-ink-3 shrink-0" aria-hidden />
            {albumOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                disabled={busy}
                onClick={() => chooseAlbum(opt.id)}
                data-testid={`upload-modal-album-${opt.id}`}
                data-selected={targetAlbumId === opt.id ? "true" : "false"}
                className={cn(
                  "shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-body-sm press transition-colors",
                  targetAlbumId === opt.id
                    ? opt.is_system
                      ? "bg-you/15 text-you-strong"
                      : "bg-gold/15 text-ink-1"
                    : "bg-paper text-ink-2 hover:bg-paper-deep",
                )}
              >
                {opt.name}
              </button>
            ))}
          </div>
        </div>

        <input
          ref={fileInput}
          type="file"
          accept={ALLOWED_MIMES.join(",")}
          multiple
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
          data-testid="upload-modal-input"
        />

        <div className="flex items-center justify-between">
          <span className="font-label text-ink-2">
            檔案佇列 ({queue.length})
            {queue.length > 0 ? (
              <span className="ml-2 text-meta text-ink-3">
                {doneCount > 0 ? `${doneCount} 完成` : ""}
                {errorCount > 0 ? ` · ${errorCount} 失敗` : ""}
              </span>
            ) : null}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={pickFiles}
            disabled={busy}
            data-testid="upload-modal-pick"
            leadingIcon={<Camera className="w-3.5 h-3.5" />}
          >
            選擇檔案
          </Button>
        </div>

        {queue.length > 0 ? (
          <ul className="flex flex-col gap-2" data-testid="upload-modal-queue">
            {queue.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center gap-2 rounded-md bg-paper-elevated px-3 py-2"
                data-testid={`upload-queue-${entry.id}`}
                data-state={entry.state}
              >
                <FileStateIcon state={entry.state} />
                <div className="flex-1 min-w-0">
                  <p className="text-body-sm text-ink-1 truncate">{entry.name}</p>
                  {/* `role="alert"` on the failure line. The modal-level
                      `role="alert"` below only fires for a client-side
                      rejection; a server-side failure used to land in a plain
                      `text-meta text-ink-3` paragraph, so nothing was announced
                      and a screen-reader user learned the upload had failed only
                      by going looking for the photo. It also gets the error
                      colour: a failed upload is the one thing in this list the
                      user must not scroll past. */}
                  {entry.error ? (
                    <p role="alert" className="text-meta text-partner-strong">
                      {entry.error}
                    </p>
                  ) : (
                    <p className="text-meta text-ink-3">
                      {entry.originalSize
                        ? `${kb(entry.originalSize)} → ${kb(entry.size)}`
                        : kb(entry.size)}
                    </p>
                  )}
                </div>
                {entry.state === "uploading" ? (
                  <div className="h-1 w-16 bg-paper-deep rounded-full overflow-hidden">
                    <div className="h-full bg-you-strong shimmer-bar" style={{ width: "100%" }} />
                  </div>
                ) : entry.state === "queued" ? (
                  <button
                    type="button"
                    aria-label="移除"
                    onClick={() => remove(entry.id)}
                    className="press inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-3 hover:bg-paper"
                  >
                    <X size={16} aria-hidden />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="rounded-md border border-dashed border-hairline px-4 py-6 text-center text-body-sm text-ink-3">
            拖放或點擊「選擇檔案」上傳照片
          </div>
        )}

        {error ? (
          <p
            role="alert"
            className="text-body-sm text-partner-strong"
            data-testid="upload-modal-error"
          >
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 justify-end pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
              reset();
            }}
            disabled={busy}
          >
            {queue.length > 0 && !busy ? "完成" : "取消"}
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
}

/** `4194304 → "4.0 MB"`, `409600 → "400 KB"` — so the arrow reads as "smaller". */
function kb(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

function FileStateIcon({ state }: { state: FileState }) {
  if (state === "uploading") {
    return <Loader2 size={18} className="text-you-strong animate-spin shrink-0" aria-hidden />;
  }
  if (state === "done") {
    return <CheckCircle2 size={18} className="text-success shrink-0" aria-hidden />;
  }
  if (state === "error") {
    return <AlertCircle size={18} className="text-partner-strong shrink-0" aria-hidden />;
  }
  return <Camera size={18} className="text-ink-3 shrink-0" aria-hidden />;
}
